/**
 * O número do que pede ação, no menu do topo — spec 0098, DD-03.
 *
 * A aba declara uma chave (`Aba.contador`, em `menu.ts`), e o setor registra
 * aqui de onde o número vem. A casca pergunta pela chave e não sabe o que cada
 * setor conta: a roteirização registra a leitura da visão geral dela.
 *
 * A fonte é ligada enquanto a casca está no setor dela, e só então consulta o
 * servidor. A fonte pode chegar depois da casca (ela mora no pedaço do bundle
 * da tela, carregado sob demanda): quem já pediu é avisado quando ela chega.
 *
 * Um setor pode ter mais de uma fonte, cada uma com o seu nome (spec 0111,
 * DD-18): o Corporativo conta o Feedback e as planilhas das escolas, e as
 * chaves das duas se somam. Registrar de novo com o mesmo nome substitui.
 */
import { useEffect, useState } from 'react';

export interface FonteDeContadores {
  /** O número de cada chave agora (zero ou ausente: sem número). */
  ler: () => Record<string, number>;
  /** Avisa quando o número muda; devolve como parar de avisar. */
  assinar: (aviso: () => void) => () => void;
  /** Começa a consultar; devolve como parar. Mais de um pode ligar ao mesmo tempo. */
  ligar: () => () => void;
}

const FONTES: Record<string, Map<string, FonteDeContadores>> = {};
const COMPOSTAS: Record<string, FonteDeContadores | undefined> = {};
const quemEspera = new Set<() => void>();
const NENHUM: Record<string, number> = {};

export function registrarFonteDeContadores(setor: string, fonte: FonteDeContadores, nome = setor): void {
  (FONTES[setor] ??= new Map()).set(nome, fonte);
  delete COMPOSTAS[setor];
  quemEspera.forEach((avisar) => avisar());
}

/** As fontes de um setor como uma só: as chaves se somam, e ligar liga todas. A mesma enquanto ninguém registra. */
export function fonteDoSetor(setor: string | undefined): FonteDeContadores | undefined {
  if (!setor) return undefined;
  if (!(setor in COMPOSTAS)) COMPOSTAS[setor] = compor([...(FONTES[setor]?.values() ?? [])]);
  return COMPOSTAS[setor];
}

function compor(fontes: FonteDeContadores[]): FonteDeContadores | undefined {
  if (fontes.length === 0) return undefined;
  if (fontes.length === 1) return fontes[0];
  return {
    ler: () => Object.assign({}, ...fontes.map((f) => f.ler())),
    assinar: (aviso) => {
      const paradas = fontes.map((f) => f.assinar(aviso));
      return () => paradas.forEach((parar) => parar());
    },
    ligar: () => {
      const desligadas = fontes.map((f) => f.ligar());
      return () => desligadas.forEach((desligar) => desligar());
    },
  };
}

/** Os números do setor em que a casca está; nada fora de setor com fonte. */
export function useContadores(setor: string | undefined): Record<string, number> {
  const [fonte, setFonte] = useState<FonteDeContadores | undefined>(() => fonteDoSetor(setor));
  const [, setVersao] = useState(0);

  useEffect(() => {
    const atualizar = () => setFonte(fonteDoSetor(setor));
    atualizar();
    quemEspera.add(atualizar);
    return () => { quemEspera.delete(atualizar); };
  }, [setor]);

  useEffect(() => {
    if (!fonte) return undefined;
    const desligar = fonte.ligar();
    const parar = fonte.assinar(() => setVersao((v) => v + 1));
    return () => { parar(); desligar(); };
  }, [fonte]);

  return fonte ? fonte.ler() : NENHUM;
}
